import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { TabType } from '../components/Layout/Navigation';

const VALID_TABS: ReadonlySet<string> = new Set<TabType>([
  'calendar', 'settings', 'faq', 'help', 'hr', 'hr-settings', 'manager', 'orgchart', 'employee', 'approvals',
]);

function isValidTab(value: string): value is TabType {
  return VALID_TABS.has(value);
}

/** Tab aus dem URL-Hash lesen, Fallback 'employee' (Meine Uebersicht als Default fuer alle Rollen). */
function tabFromHash(): TabType {
  const hash = window.location.hash.replace('#', '');
  return isValidTab(hash) ? hash : 'employee';
}

interface NavigationContextType {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  helpSection: string | null;
  navigateToHelp: (section?: string) => void;
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export function NavigationProvider({ children }: { children: ReactNode }) {
  const [activeTab, _setActiveTab] = useState<TabType>(tabFromHash);
  const [helpSection, setHelpSection] = useState<string | null>(null);
  // Ref spiegelt activeTab, damit setActiveTab keinen Seiteneffekt im
  // State-Updater ausfuehren muss (React darf Updater mehrfach callen).
  const activeTabRef = useRef(activeTab);
  activeTabRef.current = activeTab;

  // Kleiner Helper — State + Ref zusammen aktualisieren, damit
  // activeTabRef nie stale bleibt (sonst fällt der No-op-Guard
  // in setActiveTab beim naechsten Aufruf falsch aus).
  const updateTab = useCallback((tab: TabType) => {
    activeTabRef.current = tab;
    _setActiveTab(tab);
  }, []);

  // Tab wechseln + History-Eintrag erstellen.
  // No-op-Guard: kein pushState wenn der Tab unveraendert bleibt, sonst
  // entstehen Duplikate im Browser-History (Klick auf aktiven Tab).
  const setActiveTab = useCallback((tab: TabType) => {
    if (activeTabRef.current === tab) return;
    window.history.pushState({ tab }, '', `#${tab}`);
    updateTab(tab);
  }, [updateTab]);

  // Hilfe-Navigation mit pushState
  const navigateToHelp = useCallback((section?: string) => {
    setHelpSection(section ?? null);
    window.history.pushState({ tab: 'help' }, '', '#help');
    updateTab('help');
  }, [updateTab]);

  // Mount-Once: initial-History-Eintrag ersetzen.
  useEffect(() => {
    window.history.replaceState({ tab: activeTabRef.current }, '', `#${activeTabRef.current}`);
  }, []);

  // Browser-Zurück/Vor: popstate Event.
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (event.state?.tab && isValidTab(event.state.tab)) {
        updateTab(event.state.tab);
      } else {
        updateTab(tabFromHash());
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [updateTab]);

  return (
    <NavigationContext.Provider value={{ activeTab, setActiveTab, helpSection, navigateToHelp }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}
